import { createSelector } from 'reselect';
import ROUTES from 'routes/constants';
import { SECTION_TO_SLUG_MAP, SORTED_SECTIONS, STATUS } from './constants';

const selectPropertyAssessment = state => state.propertyAssessment;

const selectAssessments = createSelector(
  selectPropertyAssessment,
  propertyAssessment => propertyAssessment.assessments,
);

const selectLoaded = createSelector(
  selectPropertyAssessment,
  propertyAssessment => propertyAssessment.loaded,
);

export const makeSelectHasLoadedAssessment = photoSessionId =>
  createSelector(selectLoaded, loaded => loaded?.[photoSessionId]);

export const makeSelectAssessment = photoSessionId =>
  createSelector(selectAssessments, assessments => assessments[photoSessionId]);

const makeSelectSections = photoSessionId =>
  createSelector(
    makeSelectAssessment(photoSessionId),
    assessment => assessment.sections,
  );

export const makeSelectCurrentSectionId = photoSessionId =>
  createSelector(
    makeSelectAssessment(photoSessionId),
    assessment => assessment.currentSectionId,
  );

export const getLastAnsweredSectionId = assessment => {
  if (!assessment) {
    return undefined;
  }

  const sectionIds = SORTED_SECTIONS.filter(
    sectionId => sectionId in assessment.sections
  );

  const lastAnsweredId = sectionIds
    .reverse()
    .find(sectionId => {
      const section = assessment.sections[sectionId];
      const visibleItems = Object.values(section.items).filter(item =>
        isItemVisible(assessment, item.visibleIf),
      );

      return visibleItems.find(item => item.value?.length > 0);
    });

  return lastAnsweredId;
};

export const makeSelectResumeSectionId = photoSessionId =>
  createSelector(makeSelectAssessment(photoSessionId), assessment =>
    getLastAnsweredSectionId(assessment),
  );

export const makeSelectHasStartedAssessment = photoSessionId =>
  createSelector(
    makeSelectAssessment(photoSessionId),
    assessment => assessment !== undefined,
  );

export const makeSelectCurrentSection = photoSessionId =>
  createSelector(
    makeSelectSections(photoSessionId),
    makeSelectCurrentSectionId(photoSessionId),
    (sections, currentSectionId) => sections[currentSectionId],
  );

export const makeSelectProgress = photoSessionId =>
  createSelector(
    makeSelectAssessment(photoSessionId),
    makeSelectCurrentSectionId(photoSessionId),
    (assessment, currentSectionId) => {
      const sectionIds = SORTED_SECTIONS.filter(
        sectionId => sectionId in assessment.sections
      );

      const total = sectionIds.length;
      const current = sectionIds.findIndex(id => id === currentSectionId);

      return Math.round(((current + 1) / total) * 100);
    },
  );

export const isSectionValid = (assessment, section) =>
  Object.values(section.items).every(item => {
    const isItemValid = item.value?.length > 0;

    if (!item.visibleIf) {
      return isItemValid;
    }

    const { sectionId, itemId, value } = item.visibleIf;
    const dependencyValue = assessment.sections[sectionId].items[itemId].value;
    const isVisible = Array.isArray(dependencyValue)
      ? dependencyValue.includes(value)
      : dependencyValue === value;

    return isVisible ? isItemValid : true;
  });

export const makeSelectIsCurrentSectionValid = photoSessionId =>
  createSelector(
    makeSelectAssessment(photoSessionId),
    makeSelectCurrentSection(photoSessionId),
    (assessment, section) => isSectionValid(assessment, section),
  );

export const makeSelectSectionItemIds = photoSessionId =>
  createSelector(makeSelectCurrentSection(photoSessionId), section =>
    Object.keys(section.items),
  );

export const makeSelectSectionItem = (photoSessionId, itemId) =>
  createSelector(
    makeSelectCurrentSection(photoSessionId),
    section => section.items[itemId],
  );

export const isItemVisible = (assessment, visibleIf) => {
  if (!visibleIf) {
    return true;
  }

  const section = assessment.sections[visibleIf.sectionId];
  const value = section?.items[visibleIf.itemId]?.value;

  return Array.isArray(value)
    ? value.includes(visibleIf.value)
    : value === visibleIf.value;
};

export const makeSelectIsItemVisible = (photoSessionId, visibleIf) =>
  createSelector(makeSelectAssessment(photoSessionId), assessment =>
    isItemVisible(assessment, visibleIf),
  );

const getNewId = (assessment, offset) => {
  const sectionIds = SORTED_SECTIONS.filter(
    sectionId => sectionId in assessment.sections
  );

  const currentSectionIndex = sectionIds.findIndex(
    id => id === assessment.currentSectionId,
  );

  return sectionIds[currentSectionIndex + offset];
};

export const makeSelectPreviousSectionURL = photoSessionId =>
  createSelector(makeSelectAssessment(photoSessionId), assessment => {
    const sectionId = getNewId(assessment, -1);

    if (!sectionId) {
      return undefined;
    }

    const slug = SECTION_TO_SLUG_MAP[sectionId];

    return ROUTES.PROPERTY_ASSESSMENT_SECTION(photoSessionId, slug);
  });

export const makeSelectNextSectionURL = photoSessionId =>
  createSelector(makeSelectAssessment(photoSessionId), assessment => {
    const sectionId = getNewId(assessment, 1);

    if (!sectionId) {
      return undefined;
    }

    const slug = SECTION_TO_SLUG_MAP[sectionId];

    return ROUTES.PROPERTY_ASSESSMENT_SECTION(photoSessionId, slug);
  });

export const selectIsHelpDialogOpen = createSelector(
  selectPropertyAssessment,
  propertyAssessment => propertyAssessment.isHelpDialogOpen,
);

export const makeSelectIsCompleted = photoSessionId =>
  createSelector(
    makeSelectAssessment(photoSessionId),
    assessment => assessment?.status === STATUS.COMPLETED,
  );
